import { cognitoAuth } from './CognitoAuth';
import { isRetryableCognitoError } from './CognitoErrors';
import { dataResidencyManager, SupportedDataResidency } from './DataResidencyManager';
import { initializeAmplifyForDataResidency } from './DataResidencyAwareAuthHelpers';
import { refreshEndpoints } from './BackendInterface';
import { resolveCountryOfOrigin } from '@peekapak/country-origin';
import type { DataResidency } from '@peekapak/common/lib/regionAndResidency';
import type { HomeCountrySource } from '@peekapak/common/lib/homeCountryResolution';

// Sign-in routes to ONE data residency, derived deterministically from the
// resolved country of origin (explicit choice > geolocation > IP). Credentials
// are only ever sent to that region's Cognito pool - there is no cross-region
// brute-force. A transient (retryable) error is retried within the SAME region
// up to SIGN_IN_MAX_ATTEMPTS times; a definitive failure (wrong password,
// user-not-found) stops immediately, and the user corrects a wrong-region guess
// via the home-country control rather than by trying other regions.
export const SIGN_IN_MAX_ATTEMPTS = 5;
export const SIGN_IN_RETRY_BASE_DELAY_MS = 1000;
export const SIGN_IN_RETRY_MAX_DELAY_MS = 8000;
const SIGN_IN_RETRY_JITTER_MAX_MS = 250;

type SleepFunction = (delayMs: number) => Promise<void>;
type JitterFunction = () => number;

export interface SignInRetryStatus {
  attempt: number;
  maxAttempts: number;
}

export interface IntelligentSignInOptions {
  onRetry?: (status: SignInRetryStatus) => void;
  sleep?: SleepFunction;
  getJitterMs?: JitterFunction;
}

export function getSignInRetryDelayMs(retryIndex: number, jitterMs = 0): number {
  const exponentialDelay = SIGN_IN_RETRY_BASE_DELAY_MS * 2 ** retryIndex;
  return Math.min(exponentialDelay + jitterMs, SIGN_IN_RETRY_MAX_DELAY_MS);
}

function getDefaultSignInJitterMs(): number {
  return Math.floor(Math.random() * SIGN_IN_RETRY_JITTER_MAX_MS);
}

function defaultSignInSleep(delayMs: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });
}

interface AuthenticationResult {
  user: any;
  dataResidency: SupportedDataResidency;
  attemptCount: number;
  // Where the resolved region came from (chosen vs detected vs default).
  source: HomeCountrySource;
  // The resolved canonical home country (ISO alpha-3) the region derives from.
  homeCountry: string;
}

/** Bridge the canonical `DataResidency` union to the nominal frontend enum. */
function toSupportedResidency(dataResidency: DataResidency): SupportedDataResidency {
  return dataResidency === 'ARE' ? SupportedDataResidency.ARE : SupportedDataResidency.USA;
}

/**
 * Deterministic single-region sign in.
 *
 * Resolves the country of origin, derives ONE data residency, and attempts
 * authentication against only that region's Cognito pool. Transient/retryable
 * errors (HTTP 500 / Cognito InternalError) are retried within the same region
 * with exponential backoff up to SIGN_IN_MAX_ATTEMPTS; a definitive failure
 * stops immediately. There is NO cross-region fallback: a wrong-region guess is
 * corrected by the user changing their home country, not by trying other pools.
 */
export async function intelligentDataResidencySignIn(
  username: string,
  password: string,
  options: IntelligentSignInOptions = {},
): Promise<AuthenticationResult> {
  const sleep = options.sleep ?? defaultSignInSleep;
  const getJitterMs = options.getJitterMs ?? getDefaultSignInJitterMs;

  // Sign-in is an auth context, so geolocation prompting is permitted (D14). The
  // resolver still only prompts when no higher-precedence signal exists (D5).
  const origin = await resolveCountryOfOrigin({ mayPromptForGeolocation: true });
  const dataResidency = toSupportedResidency(origin.dataResidency);

  console.log(`🎯 Sign-in region resolved to ${dataResidency} from ${origin.source} (${origin.countryOfOrigin})`);

  // Configure Amplify for the single resolved region (which determines AWS region).
  await initializeAmplifyForDataResidency(dataResidency);

  let lastError: Error | null = null;
  let attemptCount = 0;

  for (let attempt = 1; attempt <= SIGN_IN_MAX_ATTEMPTS; attempt++) {
    attemptCount = attempt;

    try {
      const user = await cognitoAuth.signIn(username, password);

      // SUCCESS: record the account's real region (authoritative -> persisted).
      dataResidencyManager.persistDataResidency(dataResidency);
      refreshEndpoints();

      console.log(`✅ Sign in successful: ${dataResidency}`);

      return {
        user,
        dataResidency,
        attemptCount,
        source: origin.source,
        homeCountry: origin.countryOfOrigin,
      };
    } catch (error) {
      lastError = error as Error;

      // Only transient errors are retryable, and ONLY within this region.
      if (!isRetryableCognitoError(error) || attempt >= SIGN_IN_MAX_ATTEMPTS) {
        break;
      }

      options.onRetry?.({ attempt: attempt + 1, maxAttempts: SIGN_IN_MAX_ATTEMPTS });
      const delayMs = getSignInRetryDelayMs(attempt - 1, getJitterMs());
      console.warn(
        `[SignIn] Transient sign-in error in the ${dataResidency} region ` +
          `(attempt ${attempt}/${SIGN_IN_MAX_ATTEMPTS}); retrying after ${delayMs}ms`,
      );
      await sleep(delayMs);
    }
  }

  // A wrong-region guess now looks like a failed login, so the sign-in UX must
  // guide the user to check/correct their home country (single-region-sign-in
  // spec). Attach the resolved region/home country for that messaging.
  const enhancedError = new Error(
    `Authentication failed in the ${dataResidency} region. Last error: ${lastError?.message || 'Unknown error'}`,
  );
  (enhancedError as any).originalError = lastError;
  (enhancedError as any).dataResidencyAttempted = dataResidency;
  (enhancedError as any).homeCountry = origin.countryOfOrigin;
  (enhancedError as any).homeCountrySource = origin.source;

  throw enhancedError;
}

/**
 * Deterministic single-region sign up. Resolves the country of origin, derives
 * ONE data residency, and creates the account in only that region's Cognito
 * pool.
 */
export async function intelligentDataResidencySignUp(
  username: string,
  password: string,
  attributes: any,
): Promise<AuthenticationResult> {
  const origin = await resolveCountryOfOrigin({ mayPromptForGeolocation: true });
  const dataResidency = toSupportedResidency(origin.dataResidency);

  console.log(
    `📝 Creating new account in the ${dataResidency} region from ${origin.source} (${origin.countryOfOrigin})`,
  );

  // Configure Amplify for the resolved region (which determines AWS region).
  await initializeAmplifyForDataResidency(dataResidency);

  const enhancedAttributes = {
    ...attributes,
    'custom:dataResidency': dataResidency,
  };

  const result = await cognitoAuth.signUp({
    username,
    password,
    attributes: enhancedAttributes,
  });

  // The account now exists in this region (authoritative -> persisted).
  dataResidencyManager.persistDataResidency(dataResidency);
  refreshEndpoints();

  console.log(`✅ Sign up successful in the ${dataResidency} region`);

  return {
    user: result,
    dataResidency,
    attemptCount: 1,
    source: origin.source,
    homeCountry: origin.countryOfOrigin,
  };
}

/**
 * Predict the optimal data residency for a user without authenticating, by
 * delegating to the shared country-of-origin resolver. Used by the
 * forgot-password flow. Geolocation is not prompted here (`false`) - the flow is
 * an email-entry step, not a full auth screen.
 *
 * NOTE: `email` is currently unused - the resolver keys off client-side signals
 * (explicit choice > geolocation > IP), not the typed email. The former
 * per-email residency cache was removed in task 4.7 as write-only dead code.
 */
export async function predictOptimalDataResidency(email: string): Promise<{
  dataResidency: SupportedDataResidency;
  source: HomeCountrySource;
}> {
  void email;
  const origin = await resolveCountryOfOrigin({ mayPromptForGeolocation: false });
  return {
    dataResidency: toSupportedResidency(origin.dataResidency),
    source: origin.source,
  };
}

// Export types for other modules to use
export type { AuthenticationResult };

// Legacy exports for backward compatibility
export { intelligentDataResidencySignIn as intelligentRegionSignIn };
export { intelligentDataResidencySignUp as intelligentRegionSignUp };
export { predictOptimalDataResidency as predictOptimalRegion };
